/**
 * <ToolUseBlock> — central dispatcher for tool_use content blocks.
 * Maps `block.name` (the function name the model invoked) onto a
 * tool-specific render component. Falls back to <CollapsedToolJson>
 * for unrecognized tool names so a new tool never silently disappears
 * from the chat surface.
 *
 * Adding a new tool render: insert one `if (block.name === "...")`
 * branch returning the new dedicated component.
 *
 * See ai-docs/lexiun-chat-content-model-plan.md §3.
 */
import type { ToolUseBlock as ToolUseBlockType } from "@/types/content-blocks";
import { todosFromToolUse } from "@/types/content-blocks";
import { TodoCard } from "@/components/chat/TodoCard";
import { EditBlock } from "@/components/chat/EditBlock";
import { WriteBlock } from "@/components/chat/WriteBlock";
import { ReadBadge } from "@/components/chat/ReadBadge";
import { OpenedFileBadge } from "@/components/chat/OpenedFileBadge";
import { BrowserNavigateBlock } from "@/components/chat/BrowserNavigateBlock";
import { ListBadge } from "@/components/chat/ListBadge";
import { SystemStepBadge } from "@/components/chat/SystemStepBadge";
import { SYSTEM_STEP_NAME } from "@/lib/chat/tool-activity";
import { CollapsedToolJson } from "@/components/chat/CollapsedToolJson";

interface ToolUseBlockProps {
  block: ToolUseBlockType;
  runId?: string;
}

export function ToolUseBlock({ block, runId }: ToolUseBlockProps) {
  // Plan tools → TodoCard
  if (block.name === "update_plan" || block.name === "lx_todo") {
    const todos = todosFromToolUse(block);
    if (todos.length === 0) {
      // Empty plan → fall back to JSON so the visual isn't an empty card.
      return <CollapsedToolJson block={block} />;
    }
    return <TodoCard todos={todos} runId={runId ?? block.id} />;
  }
  // File-edit tools → side-by-side diff (the user-shared visual)
  if (block.name === "lx_edit" || block.name === "lx_doc_patch") {
    return <EditBlock block={block} />;
  }
  // File-create tools → new-file card
  if (block.name === "lx_write" || block.name === "lx_doc_write") {
    return <WriteBlock block={block} />;
  }
  // Read → compact badge
  if (block.name === "lx_read") {
    return <ReadBadge block={block} />;
  }
  // Open-in-pane → compact confirmation badge
  if (block.name === "lx_open_file") {
    return <OpenedFileBadge block={block} />;
  }
  // System tier → the one fixed phrase, never a path (DS-37). First branch on
  // purpose: nothing below may see a system step.
  if (block.name === SYSTEM_STEP_NAME) {
    return <SystemStepBadge />;
  }
  // Listing / searching → compact chip. Without this they fell through to the
  // JSON fallback and dumped raw args into the conversation, which reads as dev
  // output beside the file chips (operator, 2026-08-13).
  if (
    block.name === "lx_list" ||
    block.name === "lx_search" ||
    block.name === "web_search" ||
    block.name === "web_fetch" ||
    block.name === "lx_help" ||
    block.name === "lx_move_resource"
  ) {
    return <ListBadge block={block} />;
  }
  // Native upstream browser tool, "navigate" sub-action → globe badge + URL.
  // Other browser sub-actions (click, type, snapshot, …) fall through
  // to the CollapsedToolJson default since they don't have a single
  // self-explanatory field to surface.
  if (
    block.name === "browser" &&
    (block.input as { action?: unknown } | undefined)?.action === "navigate"
  ) {
    return <BrowserNavigateBlock block={block} />;
  }
  // Anything else (web_search, lx_search, native tools, future tools)
  // → collapsed JSON. Future per-tool renders plug in above this line.
  return <CollapsedToolJson block={block} />;
}
